// DrawRectTool — click+drag to draw a rectangle, then create a shape via callback
// Replaces ZoneDrawingHandler's zone drawing mode

import * as THREE from 'three'
import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import { DPM } from '@/constants/coordinates'
import { getViewer, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export interface DrawRectConfig {
  /** Called when drawing finishes. Returns new shape ID. */
  onCreate: (rect: { x: number; y: number; width: number; height: number }) => string
  /** Preview color (default: 0x4ecca3) */
  previewColor?: number
  /** Optional dimension overlay for live measurements */
  overlay?: DimensionOverlay
}

/** Minimum size in world meters to create a shape */
const MIN_SIZE_WORLD = 0.5

export class DrawRectTool implements Tool {
  readonly name = 'draw-rect'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.drawRect'

  private toolManager: ToolManager
  private config: DrawRectConfig

  // Drawing state
  private isDrawing = false
  private startWorld: THREE.Vector3 | null = null

  // Preview
  private previewGroup: THREE.Group
  private previewMesh: THREE.Mesh | null = null

  constructor(toolManager: ToolManager, scene: THREE.Scene, config: DrawRectConfig) {
    this.toolManager = toolManager
    this.config = config

    this.previewGroup = new THREE.Group()
    this.previewGroup.name = 'draw-rect-preview'
    scene.add(this.previewGroup)
  }

  activate(): void {
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('DrawRectTool', 'Activated')
  }

  deactivate(): void {
    this.cancelDrawing()
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('DrawRectTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.worldPoint) return

    this.isDrawing = true
    this.startWorld = ctx.worldPoint.clone()
    this.toolManager.setControlsEnabled(false)

    ctx.event.preventDefault()
    ctx.event.stopPropagation()

    debug.log('DrawRectTool', `Start at (${ctx.worldPoint.x.toFixed(2)}, ${ctx.worldPoint.z.toFixed(2)})`)
  }

  onMouseMove(ctx: ToolContext): void {
    if (!this.isDrawing || !this.startWorld || !ctx.worldPoint) return
    this.updatePreview(this.startWorld, ctx.worldPoint, ctx.camera)
  }

  onMouseUp(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!this.isDrawing || !this.startWorld) {
      this.cancelDrawing()
      return
    }

    const endWorld = ctx.worldPoint
    if (!endWorld) {
      this.cancelDrawing()
      return
    }

    // Calculate bounds in world space
    const minX = Math.min(this.startWorld.x, endWorld.x)
    const maxX = Math.max(this.startWorld.x, endWorld.x)
    const minZ = Math.min(this.startWorld.z, endWorld.z)
    const maxZ = Math.max(this.startWorld.z, endWorld.z)

    const widthWorld = maxX - minX
    const depthWorld = maxZ - minZ

    // Check minimum size
    if (widthWorld < MIN_SIZE_WORLD || depthWorld < MIN_SIZE_WORLD) {
      debug.log('DrawRectTool', 'Too small, cancelled')
      this.cancelDrawing()
      return
    }

    // Convert to editor coords: center + size in dots
    const centerX = ((minX + maxX) / 2) * DPM
    const centerY = ((minZ + maxZ) / 2) * DPM

    // Create shape via callback
    const id = this.config.onCreate({
      x: Math.round(centerX),
      y: Math.round(centerY),
      width: Math.round(widthWorld * DPM),
      height: Math.round(depthWorld * DPM)
    })

    debug.log('DrawRectTool', `Created shape: ${id}`)

    // Clean up and switch back to select tool
    this.cancelDrawing()
    this.toolManager.setActiveTool('select')
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelDrawing()
      this.toolManager.setActiveTool('select')
    }
  }

  dispose(): void {
    this.cancelDrawing()
    this.previewGroup.parent?.remove(this.previewGroup)
  }

  // --- Private ---

  private cancelDrawing(): void {
    if (this.isDrawing) {
      this.toolManager.setControlsEnabled(true)
    }
    this.isDrawing = false
    this.startWorld = null
    this.clearPreview()
    this.config.overlay?.hideAll()
  }

  private updatePreview(start: THREE.Vector3, end: THREE.Vector3, camera?: THREE.Camera): void {
    this.clearPreview()

    const minX = Math.min(start.x, end.x)
    const maxX = Math.max(start.x, end.x)
    const minZ = Math.min(start.z, end.z)
    const maxZ = Math.max(start.z, end.z)

    const width = maxX - minX
    const depth = maxZ - minZ

    if (width < 0.05 || depth < 0.05) return

    const geometry = new THREE.PlaneGeometry(width, depth)
    const material = new THREE.MeshBasicMaterial({
      color: this.config.previewColor ?? 0x4ecca3,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    this.previewMesh = new THREE.Mesh(geometry, material)
    this.previewMesh.rotation.x = -Math.PI / 2
    this.previewMesh.position.set(
      (minX + maxX) / 2,
      0.02,
      (minZ + maxZ) / 2
    )

    this.previewGroup.add(this.previewMesh)

    // Show live measurement overlays
    if (camera && this.config.overlay) {
      this.config.overlay.showDimensions(width, depth, maxX, maxZ, camera)
      this.config.overlay.showArea(width * depth, (minX + maxX) / 2, (minZ + maxZ) / 2, camera)
    }
  }

  private clearPreview(): void {
    if (this.previewMesh) {
      this.previewGroup.remove(this.previewMesh)
      this.previewMesh.geometry.dispose()
      if (this.previewMesh.material instanceof THREE.Material) {
        this.previewMesh.material.dispose()
      }
      this.previewMesh = null
    }
  }
}
